import { useState } from "react";
import VertragDokumentVorschau from "../VertragDokumentVorschau";
import AngebotSenden from "../vertrag/AngebotSenden";
import { Row, MonateFehlen, Kalkbox, Pruefliste, erstesJahrBrutto } from "./teile";
import { eur } from "../../hooks/useVertraege";
import type { VertragPruefung } from "../../hooks/useVertraege";
import { planNetto } from "../../lib/wizard";
import type { kalkVorschau, VertragPosition } from "../../lib/wizard";
import { zahl } from "../../lib/zahl";

const FAKTOR_PRESETS = [0.9, 1.0, 1.1, 1.25];

/** Schritt 4 „Preis": ein Faktor auf alle Einzelpreise. */
export function SchrittPreis({ faktor, setFaktor, kalk, ohneMonate }: {
  faktor: number; setFaktor: (f: number) => void;
  kalk: ReturnType<typeof kalkVorschau>; ohneMonate: number;
}) {
  return (
    <>
      <div className="pf-wz-titel pf-wz-unten-8">Preisfaktor: ×{zahl(faktor, 2)}</div>
      <input type="range" min={0.8} max={1.5} step={0.05} value={faktor}
        onChange={e => setFaktor(Number(e.target.value))} className="pf-wz-regler" />
      <div className="pf-wz-faktoren">
        {FAKTOR_PRESETS.map(f => (
          <button key={f} className={"pf-btn pf-wz-faktor" + (faktor === f ? " is-on" : "")}
            onClick={() => setFaktor(f)}>×{zahl(f, 2)}</button>
        ))}
      </div>
      <Kalkbox kalk={kalk} />
      {ohneMonate > 0 && (
        <div className="pf-card pf-wz-oben-10"><MonateFehlen n={ohneMonate} /></div>
      )}
    </>
  );
}

const MONAT_KURZ = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
                    "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

/**
 * Schritt 5 „Prüfen und senden": Beginn, Sondervereinbarungen, Zusammenfassung,
 * Prüfliste und — sobald „Angebot senden" gedrückt ist — die Mail zum Ansehen.
 */
export function SchrittSenden({ v, entwurfId, mailOffen, onMailZu, onGesendet, onVerwerfen, verwerfenLaeuft }: {
  v: {
    name?: string; beginn: string; setBeginn: (s: string) => void; sonder: string; setSonder: (s: string) => void;
    laufzeit: number; beginnJahr: string; kuend: number; istVerbraucher: boolean; faktor: number;
    positionen: VertragPosition[]; kalk: ReturnType<typeof kalkVorschau>; ohneMonate: number;
    pruefung?: VertragPruefung; fehler: string;
  };
  entwurfId: string;
  mailOffen: boolean;
  onMailZu: () => void;
  onGesendet: () => void;
  onVerwerfen: () => void;
  verwerfenLaeuft: boolean;
}) {
  // Zugeklappt: die Vorschau misst die Seitenzahl und erzeugt dafür ein PDF.
  const [vorschau, setVorschau] = useState(false);
  const { beginn, beginnJahr, laufzeit, kalk } = v;
  // Das erste Jahr kostet die Einsätze ab dem Beginnmonat (26.10.2026).
  const beginnMonat = beginn ? Number(beginn.slice(5, 7)) : 1;
  const rumpf = planNetto(v.positionen, v.faktor, beginnMonat);
  const vonBis = `${MONAT_KURZ[beginnMonat - 1]}–Dez`;

  return (
    <>
      <div className="pf-card pf-wz-abstand">
        <div className="pf-grid-2">
          <label className="pf-field"><span>Vertragsbeginn</span>
            <input className="pf-input" type="date" value={beginn}
              onChange={e => v.setBeginn(e.target.value)} /></label>
          <label className="pf-field"><span>Erstes Vertragsjahr</span>
            <div className={"pf-input pf-wz-anzeige" + (beginn ? "" : " is-leer")}>
              {beginn
                ? `bis 31.12.${beginnJahr} · ${laufzeit} ${laufzeit === 1 ? "Monat" : "Monate"}`
                : "folgt aus dem Beginn"}
            </div>
          </label>
        </div>
        <label className="pf-field pf-wz-oben-6">
          <span>Sondervereinbarungen — nur für diesen Garten</span>
          <textarea className="pf-input" rows={4} value={v.sonder}
            onChange={e => v.setSonder(e.target.value)}
            placeholder={"Zugang: Schlüssel liegt im Briefkasten.\n\nTeich: bleibt außen vor."} />
        </label>
        <div className="pf-wz-klein pf-wz-zeilen">
          Je Absatz „Stichwort: Text", Absätze durch eine Leerzeile getrennt. Steht im
          Vertrag und beim Kunden in der Kurzübersicht („Für Ihren Garten"). Was für
          alle Gärten gilt (Termine, Wetter, Nachweis), steht dort schon.
        </div>
      </div>
      <div className="pf-card pf-wz-abstand">
        <div className="pf-wz-titel pf-wz-gross">{v.name}</div>
        <Row l="Leistungen" r={`${v.positionen.filter(p => p.beauftragt || !p.optional).length} Positionen`} />
        <Row l="Monatsrate" r={`${eur(kalk.monats_netto)} netto`} strong />
        <Row l="Jahreswert" r={`${eur(kalk.jahres_netto)} netto`} />
        {beginn && laufzeit < 12 && (
          <Row l={`Kunde zahlt ${beginnJahr}`}
               r={rumpf > 0
                 ? `${eur(erstesJahrBrutto(rumpf))} brutto (Einsätze ${vonBis})`
                 : `nichts — bis Dezember ist kein Einsatz geplant, erste Rate im Januar`} />
        )}
        {beginn && laufzeit < 12 && rumpf > 0 && (
          <div className="pf-wz-klein">
            Das erste Vertragsjahr kostet, was ab {MONAT_KURZ[beginnMonat - 1]} geplant ist,
            verteilt auf {laufzeit} {laufzeit === 1 ? "Rate" : "Raten"} bei monatlicher Zahlung.
            Was tatsächlich geleistet wurde, gleicht die Jahresabrechnung aus.
          </div>
        )}
        <Row l="Erstes Vertragsjahr" r={beginn
          ? `bis 31.12.${beginnJahr} (${laufzeit} Mon.) · Kündigung ${v.kuend} Mon.`
          : `Kündigung ${v.kuend} Mon.`} />
        <Row l="Zahlweise" r="wählt der Kunde auf seiner Angebotsseite" />
        <Row l="Vertragsart" r={v.istVerbraucher ? "Privatkunde (Verbraucher)" : "Firma / Unternehmer"} />
        <Row l="DB / Stunde" r={kalk.db_pro_stunde ? eur(kalk.db_pro_stunde) : "—"} />
        {v.ohneMonate > 0 && <MonateFehlen n={v.ohneMonate} />}
      </div>
      {v.pruefung && <Pruefliste p={v.pruefung} />}
      {v.fehler && <div className="pf-wz-fehler pf-wz-unten">{v.fehler}</div>}
      <p className="pf-senden-hinweis">
        Der Entwurf ist gespeichert. „Angebot senden" vergibt die Vertragsnummer
        und zeigt die Mail, bevor sie rausgeht. Darin steht nur der Kundenlink;
        die Abschluss-PIN gibst du dem Kunden persönlich, sie steht danach auf
        der Vertragsseite. Mit der Zustimmung wird der Vertrag von selbst aktiv.
      </p>
      {mailOffen && entwurfId && (
        <div className="pf-wz-unten-14">
          <AngebotSenden vertragId={entwurfId} abbrechenText="Noch nicht senden"
            onGesendet={onGesendet} onAbbrechen={onMailZu} />
        </div>
      )}
      <div className="pf-wz-knoepfe">
        {/* Ansehen, bevor es jemand bekommt — und zwar unter den
            Entscheidungen, die der Kunde treffen wird, nicht nur unter
            der einen, die gerade gespeichert ist. */}
        <button className="pf-btn is-ghost is-small" onClick={() => setVorschau(x => !x)}>
          {vorschau ? "Vorschau schließen" : "So sieht es der Kunde"}
        </button>
        <button className="pf-btn is-ghost is-small pf-wz-rot" onClick={onVerwerfen} disabled={verwerfenLaeuft}>
          Entwurf verwerfen
        </button>
      </div>
      {vorschau && entwurfId && (
        <div className="pf-wz-oben-14">
          <VertragDokumentVorschau vertragId={entwurfId} />
        </div>
      )}
    </>
  );
}
